/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useEffect } from "react";
import { Tooltip } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CalendarMonthTwoToneIcon from "@mui/icons-material/CalendarMonthTwoTone";
import DatePicker from "react-multi-date-picker";
import DialogLayout from "../../Components/DialogLayout";
import useTailwindBrand from "../../Theme/useTailwindBrand";
import { months, weekDays } from "../../Utils/Function";
import { STATUS_OPTIONS } from "../../config/COURSE_TYPE_CONFIG";
import { useClientListCourse, ClientCourseType } from "../Courses/Client/useClientListCourse";
import { DV2 } from "../DeclarationV2/theme";
import { euros, hoursFromHalfHours, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Avatar, Card, TextLink, fieldStyle, useDv2Theme } from "../DeclarationV2/layout/Ui";
import ClientCourseDetailV2, { ClientCourseDetailHeader } from "./ClientCourseDetailV2";
import { clientCourseStatusDetail, clientCourseStatusNode } from "./clientCourseStatus";
import {
    courseChildName, courseDatesLabel, courseDatesSubline, coursePaid, courseSubjectLine, courseTeachrInitials, courseTeachrName, courseTotalTtc, paidLabel,
} from "./clientCourseRows";

interface Props {
    type: ClientCourseType;
    /** Avance immédiate active : la colonne montre ce que paie le client, moitié du prix. */
    aiciActive: boolean;
    clientId?: number;
    /** Nombre total de la liste courante, remonté à la page (sous-titre). */
    onTotal?: (total: number) => void;
}

const GRID = "minmax(0,1.9fr) minmax(0,1.6fr) minmax(0,0.8fr) minmax(0,1.1fr) minmax(0,1.4fr) 44px";
const PAGE_SIZES = [5, 10, 25];

/**
 * Tableau « Mes cours » du client repensé : mêmes données et même hook
 * (`useClientListCourse` : filtres, pagination côté serveur) que la liste
 * actuelle, avec les colonnes de l'espace client (professeur et matière,
 * dates, durée, ce que vous payez, statut) et la fiche du cours en fenêtre.
 * En lecture seule : le client ne modifie pas un cours, il le valide ou le
 * conteste auprès de l'URSSAF.
 */
export default function ClientCourseTableV2({ type, aiciActive, clientId, onTotal }: Props) {
    const { primary, fontFamily, text } = useDv2Theme();
    const { classes, brand } = useTailwindBrand();
    const {
        page, rowsPerPage,
        nameSearch, setNameSearch, statusSearch, setStatusSearch, dateValues, setDateValues,
        selectedCourse, setSelectedCourse, detailOpen, setDetailOpen,
        isLoading, courses, totalItems, hasActiveFilters,
        handleChangePage, handleChangeRowsPerPage, handleOpenDetail, clearFilters,
    } = useClientListCourse(type);
    const isHistorique = type === "historique";

    useEffect(() => { if (!isLoading) onTotal?.(totalItems); }, [isLoading, totalItems]);

    const closeDetail = () => { setDetailOpen(false); setSelectedCourse(null); };
    const pageCount = Math.max(1, Math.ceil(totalItems / rowsPerPage));
    const pages = (): Array<number | "…"> => {
        if (pageCount <= 6) return Array.from({ length: pageCount }, (_, i) => i);
        const set = new Set<number>([0, 1, 2, page - 1, page, page + 1, pageCount - 1].filter((p) => p >= 0 && p < pageCount));
        const sorted = Array.from(set).sort((a, b) => a - b);
        const result: Array<number | "…"> = [];
        sorted.forEach((p, i) => { if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("…"); result.push(p); });
        return result;
    };

    const amountCell = (c: any) => (
        <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{euros(coursePaid(c, aiciActive))}</div>
            <div style={{ fontSize: 12, color: DV2.muted }}>{aiciActive ? `sur ${euros(courseTotalTtc(c))} TTC` : "TTC"}</div>
        </div>
    );
    const detailButton = (c: any) => (
        <Tooltip title={`Voir ${vocab.theCourse()}`}>
            <button type="button" aria-label={`Voir ${vocab.theCourse()} avec ${courseTeachrName(c)}`} onClick={() => handleOpenDetail(c)} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${DV2.border}`, background: "#fff", color: primary, display: "grid", placeItems: "center", cursor: "pointer" }}>
                <RemoveRedEyeOutlinedIcon fontSize="small" />
            </button>
        </Tooltip>
    );

    const emptyText = isHistorique
        ? `Aucun ${vocab.course()} passé pour le moment.`
        : `Aucun ${vocab.course()} à venir pour le moment. Les ${vocab.courses()} déclarés par ${vocab.yourTeachr()} apparaîtront ici.`;

    return (
        <>
            <Card padding={0} style={{ overflow: "hidden", fontFamily, color: text }}>
                {/* Filtres */}
                <div className="flex flex-col lg:flex-row lg:items-center gap-3" style={{ padding: "14px 20px", borderBottom: `1px solid ${DV2.borderSoft}` }}>
                    <input
                        type="search"
                        value={nameSearch}
                        onChange={(e) => setNameSearch(e.target.value)}
                        placeholder={`Filtrer par ${vocab.teachr()}`}
                        aria-label={`Filtrer par ${vocab.teachr()}`}
                        style={{ ...fieldStyle(), background: DV2.pageBg, fontWeight: 500, fontSize: 14, maxWidth: 320 }}
                    />
                    <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                        {isHistorique && (
                            <select value={statusSearch} onChange={(e) => setStatusSearch(e.target.value)} aria-label="Filtrer par statut" style={{ ...fieldStyle(), width: "auto", fontSize: 13, fontWeight: 700, padding: "8px 12px", fontFamily, appearance: "auto" as any }}>
                                <option value="">Tous les statuts</option>
                                {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        )}
                        <div className="flex items-center gap-2" style={{ ...fieldStyle(), width: "auto", padding: "4px 10px" }}>
                            <CalendarMonthTwoToneIcon sx={{ color: DV2.muted, fontSize: 20, flexShrink: 0 }} />
                            <DatePicker
                                inputClass="custom-input"
                                placeholder="Période"
                                containerStyle={{ width: 190, border: "none" }}
                                months={months}
                                weekDays={weekDays}
                                format="DD/MM/YYYY"
                                value={dateValues}
                                onChange={(e: any) => setDateValues(e)}
                                range
                                rangeHover
                                portal
                                className={brand === "verified-trainer" ? "teal" : ""}
                            />
                        </div>
                        {hasActiveFilters && <TextLink label="Effacer les filtres" onClick={clearFilters} />}
                    </div>
                </div>

                {/* En-tête (web) */}
                <div className="hidden md:grid" style={{ gridTemplateColumns: GRID, padding: "10px 20px", fontSize: 11, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${DV2.borderSoft}`, gap: 12 }}>
                    <span>{vocab.teachr()}</span><span>Dates</span><span>Durée</span><span>{paidLabel(aiciActive)}</span><span>Statut</span><span />
                </div>

                {isLoading ? (
                    <div style={{ padding: 32, textAlign: "center", color: DV2.muted, fontSize: 14 }}>Chargement de vos {vocab.courses()}…</div>
                ) : courses.length === 0 ? (
                    <div style={{ padding: "48px 20px", textAlign: "center", color: DV2.muted, fontSize: 14 }}>
                        <div style={{ fontSize: 28 }} aria-hidden>📅</div>
                        <div style={{ marginTop: 8, maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}>{hasActiveFilters ? `Aucun ${vocab.course()} ne correspond aux filtres choisis.` : emptyText}</div>
                        {hasActiveFilters && <div style={{ marginTop: 8 }}><TextLink label="Effacer les filtres" onClick={clearFilters} /></div>}
                    </div>
                ) : courses.map((c: any) => {
                    const child = courseChildName(c);
                    const teachrCell = (
                        <div className="flex items-center gap-3 min-w-0">
                            <Avatar text={courseTeachrInitials(c)} size={38} />
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{courseTeachrName(c)}</div>
                                <div style={{ fontSize: 12, color: DV2.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{courseSubjectLine(c)}{child ? `, pour ${child}` : ""}</div>
                            </div>
                        </div>
                    );
                    return (
                        <div key={c.id ?? `${c.startDatetime}`} style={{ borderBottom: `1px solid ${DV2.borderSoft}` }}>
                            <div className="hidden md:grid items-center" style={{ gridTemplateColumns: GRID, padding: "14px 20px", gap: 12, fontSize: 14 }}>
                                {teachrCell}
                                <div>
                                    <div style={{ fontWeight: 700 }}>{courseDatesLabel(c)}</div>
                                    <div style={{ fontSize: 12, color: DV2.muted }}>{courseDatesSubline(c, !isHistorique)}</div>
                                </div>
                                <div style={{ fontWeight: 700 }}>{hoursFromHalfHours(c?.duration)}</div>
                                {amountCell(c)}
                                <div>{clientCourseStatusNode(c, isHistorique, classes)}</div>
                                <div className="flex justify-end">{detailButton(c)}</div>
                            </div>
                            <div className="md:hidden flex flex-col gap-2" style={{ padding: "14px 16px" }}>
                                <div className="flex items-start justify-between gap-2">{teachrCell}{detailButton(c)}</div>
                                <div style={{ fontSize: 13 }}>{courseDatesLabel(c)}, {courseDatesSubline(c, !isHistorique)}, {hoursFromHalfHours(c?.duration)}</div>
                                <div className="flex items-center justify-between gap-2">{amountCell(c)}<div>{clientCourseStatusNode(c, isHistorique, classes)}</div></div>
                            </div>
                        </div>
                    );
                })}

                {/* Pagination */}
                {!isLoading && totalItems > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3" style={{ padding: "14px 20px" }}>
                        <span style={{ fontSize: 13, color: DV2.muted }}>{page * rowsPerPage + 1} à {Math.min(totalItems, (page + 1) * rowsPerPage)} sur {plural(totalItems, vocab.course(), vocab.courses())}</span>
                        <div className="flex items-center gap-2">
                            <label style={{ fontSize: 12, color: DV2.muted }} className="hidden sm:flex items-center gap-2">
                                Par page
                                <select value={rowsPerPage} onChange={(e) => handleChangeRowsPerPage(e as any)} style={{ ...fieldStyle(), width: "auto", padding: "4px 8px", fontSize: 12, fontFamily, appearance: "auto" as any }}>
                                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </label>
                            <nav aria-label="Pagination" className="flex items-center gap-1">
                                {pages().map((p, i) => p === "…" ? (
                                    <span key={`gap-${i}`} style={{ width: 32, textAlign: "center", color: DV2.placeholder }}>…</span>
                                ) : (
                                    <button key={p} type="button" onClick={() => handleChangePage(null, p)} aria-current={p === page ? "page" : undefined} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${p === page ? primary : DV2.border}`, background: p === page ? primary : "#fff", color: p === page ? "#fff" : text, fontWeight: 700, fontFamily, cursor: "pointer", fontSize: 13 }}>{p + 1}</button>
                                ))}
                            </nav>
                        </div>
                    </div>
                )}
            </Card>

            {selectedCourse && (
                <DialogLayout
                    title={<ClientCourseDetailHeader course={selectedCourse} statusNode={clientCourseStatusDetail(selectedCourse, isHistorique, classes)} />}
                    text=""
                    open={detailOpen}
                    maxWidth="md"
                    handleClose={closeDetail}
                    customDialogActions="hidden"
                    handleCloseIcon={closeDetail}
                >
                    <ClientCourseDetailV2 course={selectedCourse} aiciActive={aiciActive} statusNode={null} clientId={clientId} />
                </DialogLayout>
            )}
        </>
    );
}
